import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import classNames from "classnames";

import { ProjectRedirection } from "@common/projectRedirection";
import {
  useProjectEdition,
  useProjectRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectDetailsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveStrategicAxes,
} from "@planzone/view-models";
import { Assignable } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { CloseIcon } from "@scenes/project/closeIcon";
import { Loader } from "@common/loader";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { TagList } from "@common/tagList";
import { ProjectPortfoliosList } from "@common/projectPortfoliosList";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ZoneAvatar } from "@common/zoneAvatar";
import { WeatherReport } from "@common/weatherReport";
import { WeightTag } from "@common/weightTag";
import { toFixed } from "@common/projectSection/utils";
import { SecondaryCriteria } from "./secondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Safeguard } from "@scenes/safeguard";
import { Props } from "./types";
import { NameForm } from "./nameForm";
import { DescriptionForm } from "./descriptionForm";
import { DateForm } from "./dateForm";
import { ProjectStatus } from "./projectStatus";
import { ProjectToolsConfiguration } from "./projectToolsConfiguration";
import { Resources } from "./resources";
import { ResourceList } from "./managerList";
import styles from "./styles.scss";

function Project(props: Props) {
  const { t } = useTranslation();
  const { project, isRequestPending, retrieveProject } = useProjectRetrieval(
    props.project.uuid,
  );
  const { pushView } = useModal();
  const { isRequestPending: isProjectEditionRequestPending, editProject } =
    useProjectEdition(project);
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "project",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const {
    isRequestPending: isConfigurationRequestPending,
    isRequestSuccess: isConfigurationRequestSuccess,
    retrieveProjectDetailsConfiguration,
  } = useRetrieveProjectDetailsConfiguration(planzone);
  const { strategicAxes } = useRetrieveStrategicAxes();

  React.useEffect(() => {
    !isRequestPending && retrieveProject(props.project.uuid);
  }, [props.project]);

  React.useEffect(() => {
    !isConfigurationRequestPending &&
      !isConfigurationRequestSuccess &&
      retrieveProjectDetailsConfiguration(planzone);
  }, [planzone.uuid]);

  return isRequestPending ? (
    <div className={styles.loader}>
      <div className={styles.loaderMain}>
        <Loader />
      </div>
    </div>
  ) : (
    <VisibilitySwitch
      renderChildren={(isVisible, show, hide) => (
        <div className={styles.project}>
          <div>
            <h1 className={styles.title}>
              <p>{t("projectDetails.title", { name: project.name })}</p>
              <div className={styles.headingRight}>
                <div>
                  {props.isMultiProject && (
                    <ProjectRedirection
                      className={styles.contentProjectRedirection}
                      projectId={project.uuid}
                      close={props.close}
                    />
                  )}
                </div>
                <div className={styles.closeButton} onClick={props.close}>
                  <CloseIcon className={styles.close} />
                </div>
              </div>
            </h1>
          </div>
          <div className={styles.main}>
            <div className={styles.column}>
              <div className={styles.heading}>
                <div className={styles.nameForm}>
                  <NameForm project={project} />
                </div>
              </div>
              <div className={styles.tagsGroup}>
                <div className={styles.projectPortfoliosList}>
                  <h3 className={styles.subTitle}>
                    {t("projectDetails.projectPortfoliosList")}
                  </h3>
                  <ProjectPortfoliosList
                    project={project}
                    isRemovable
                    remove={(portfolio) =>
                      pushView({
                        key: UseCases.removeProjectFromPortfolio,
                        data: { portfolio, project },
                      })
                    }
                  />
                </div>
                <div className={styles.tags}>
                  <h3 className={styles.subTitle}>
                    {t("projectDetails.tagsList")}
                  </h3>
                  <TagList
                    tags={project.tags}
                    project={project}
                    id={project.uuid}
                    type={Assignable.PROJECT}
                  />
                </div>
              </div>
              <div className={styles.section}>
                <DescriptionForm
                  project={project}
                  autoFocusDescription={props.autoFocusDescription}
                />
              </div>
              <div className={styles.secondaryCriteria}>
                <div className={styles.heading}>
                  <h3 className={styles.subTitle}>
                    {t("projectDetails.secondaryCriteria")}
                  </h3>
                  <Safeguard
                    useCases={[UseCases.editProjectDetailsConfiguration]}
                  >
                    {({ canEditProjectDetailsConfiguration }) =>
                      canEditProjectDetailsConfiguration ? (
                        <div
                          className={styles.contextualMenuIcon}
                          onClick={() =>
                            pushView({
                              key: UseCases.retrieveProjectDetailsConfiguration,
                              data: project,
                            })
                          }
                        >
                          <CogWheelIcon className={styles.configurationIcon} />
                        </div>
                      ) : (
                        <div className={styles.configurationIcon} />
                      )
                    }
                  </Safeguard>
                </div>
                <SecondaryCriteria
                  project={project}
                  criteriaForms={template.reduce(
                    (acc, criteria) => ({
                      ...acc,
                      [criteria.uuid]: (
                        <div className={styles.secondaryCriteriaForm}>
                          <SecondaryCriteriaForm
                            criteria={criteria}
                            value={
                              project.attributes
                                ? project.attributes[criteria.uuid] || ""
                                : ""
                            }
                            flexible
                            disabled={isProjectEditionRequestPending}
                            submit={(value) =>
                              editProject({
                                project,
                                nextProject: {
                                  ...project,
                                  attributes: {
                                    ...project.attributes,
                                    [criteria.uuid]: value,
                                  },
                                },
                              })
                            }
                          />
                        </div>
                      ),
                    }),
                    {
                      zone: (
                        <div className={styles.dates}>
                          {project.zone && (
                            <div className={styles.zone}>
                              <div className={styles.avatar}>
                                <ZoneAvatar
                                  name={project.zone.name}
                                  color={project.zone.color}
                                />
                              </div>
                              <p>{project.zone.name}</p>
                            </div>
                          )}
                        </div>
                      ),
                      weather: (
                        <div className={styles.weather}>
                          <WeatherReport
                            weather={project.attributes.weather}
                            className={styles.weatherIcon}
                          />
                        </div>
                      ),
                      strategicScore: (
                        <div className={styles.weather}>
                          <WeightTag
                            weight={project.averageOfConsistencyOfStrategicAxes}
                            maxWeight={strategicAxes.reduce(
                              (acc, { weight }) => acc + weight * 2,
                              0,
                            )}
                          />
                        </div>
                      ),
                      activityMonitoring_done: (
                        <p className={classNames(styles.text, styles.positive)}>
                          {toFixed(project.proportionOfAccomplishedWork)}%
                        </p>
                      ),
                      activityMonitoring_delayed: (
                        <p className={classNames(styles.text, styles.warning)}>
                          {toFixed(project.proportionOfDelayedWork)}%
                        </p>
                      ),
                      activityMonitoring_opened: (
                        <p className={styles.text}>
                          {toFixed(project.proportionOfOpenedWork)}%
                        </p>
                      ),
                      workload_allocatedTime: (
                        <p
                          className={classNames(styles.text, {
                            [styles.warning]:
                              project.totalSumOfTimeAllocatedOnTheProject < 0,
                          })}
                        >
                          {toFixed(project.totalSumOfTimeAllocatedOnTheProject)}{" "}
                          h
                        </p>
                      ),
                      workload_spentTime: (
                        <p
                          className={classNames(styles.text, {
                            [styles.warning]:
                              project.totalOfTimesSpentInProject < 0,
                          })}
                        >
                          {toFixed(project.totalOfTimesSpentInProject)} h
                        </p>
                      ),
                      workload_remainingWork: (
                        <p
                          className={classNames(styles.text, {
                            [styles.warning]:
                              project.totalOfRemainingTimeInProject < 0,
                          })}
                        >
                          {toFixed(project.totalOfRemainingTimeInProject)} h
                        </p>
                      ),
                      budget_plannedBudget: (
                        <p className={styles.text}>
                          {project.finalPlannedValueOfProject >= 1000
                            ? `${toFixed(project.finalPlannedValueOfProject / 1000)}k`
                            : project.finalPlannedValueOfProject}
                          €
                        </p>
                      ),
                      budget_realCosts: (
                        <p
                          className={classNames(styles.text, {
                            [styles.warning]:
                              project.trackingRealCost >
                              project.finalPlannedValueOfProject,
                          })}
                        >
                          {project.trackingRealCost >= 1000
                            ? `${toFixed(project.trackingRealCost / 1000)}k`
                            : project.trackingRealCost}
                          €
                        </p>
                      ),
                      budget_acquiredValue: (
                        <p className={styles.text}>
                          {project.trackingAcquiredValue >= 1000
                            ? `${toFixed(project.trackingAcquiredValue / 1000)}k`
                            : project.trackingAcquiredValue}
                          €
                        </p>
                      ),
                      budget_allocatedBudget: (
                        <p className={styles.text}>
                          {project.allocatedBudget >= 1000
                            ? `${toFixed(project.allocatedBudget / 1000)}k`
                            : project.allocatedBudget}
                          €
                        </p>
                      ),
                      budget_realMargin: (
                        <p
                          className={classNames(styles.text, {
                            [styles.warning]: project.realMarginBudget < 0,
                            [styles.positive]: project.realMarginBudget > 0,
                          })}
                        >
                          {project.realMarginBudget >= 1000 ||
                          project.realMarginBudget <= -1000
                            ? `${toFixed(project.realMarginBudget / 1000)}k`
                            : project.realMarginBudget}
                          €
                        </p>
                      ),
                      budget_plannedMargin: (
                        <p
                          className={classNames(styles.text, {
                            [styles.warning]: project.plannedMarginBudget < 0,
                            [styles.positive]: project.plannedMarginBudget > 0,
                          })}
                        >
                          {project.plannedMarginBudget >= 1000 ||
                          project.plannedMarginBudget <= -1000
                            ? `${toFixed(project.plannedMarginBudget / 1000)}k`
                            : project.plannedMarginBudget}
                          €
                        </p>
                      ),
                      projectManagers: (
                        <div className={styles.tags}>
                          <ResourceList project={project} />
                        </div>
                      ),
                    },
                  )}
                />
              </div>
            </div>
            <div className={styles.column}>
              <div className={styles.tagsGroup}>
                <div>
                  <h3 className={styles.subTitleStatus}>
                    {t("projectDetails.projectStatus")}
                  </h3>
                  <ProjectStatus
                    project={project}
                    selectStatus={(status) =>
                      !isProjectEditionRequestPending &&
                      editProject({
                        project,
                        nextProject: { ...project, status },
                      })
                    }
                  />
                </div>
                <div className={styles.dateForm}>
                  <DateForm project={project} />
                </div>
              </div>
              {planzone.uuid !== "57ec7e80-dcbb-49af-b11f-665fa6e545c1" && (
                <Resources project={project} />
              )}
              <ProjectToolsConfiguration project={project} />
            </div>
          </div>
        </div>
      )}
    />
  );
}

export { Project };
